import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';

// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Alert,
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent
} from '@mui/material';

// components

// services
import { debounce } from 'lodash';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { TenantType } from 'src/types/enums';

import { useBoolean } from 'src/hooks/use-boolean';
import ListEditorModal from 'src/sections/lists/modals/list-editor-modal';
import { ListEntityType } from '../../../services/lists/lists.types';
// types
import { useAddListItemsMutation, useGetListsPaginatedQuery } from '../../../services/lists/lists.service';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  entityType: ListEntityType;
  items: string[];
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

interface SelectableOption {
  id: string;
  name: string;
}

export default function AddItemsModal({ open, onClose, entityType, items, onSuccess }: Props) {

  const { t } = useTranslation();

  const authContext = useAuthContext();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedList, setSelectedList] = useState<SelectableOption | null>(null);

  const listEditorControl = useBoolean();

  const [addItems, { isLoading: isAddingToList }] = useAddListItemsMutation();

  const { currentData: lists, isFetching: isListsLoading } = useGetListsPaginatedQuery(
    {
      page: 1,
      per_page: 5,
      entity_type: entityType,
      search_query: searchQuery,
    },
    {
      skip: !entityType || !authContext || ![TenantType.Recruiter, TenantType.Client].includes(authContext.organization.type),
      refetchOnMountOrArgChange: true,
    }
  );

  const onSearch = useMemo(() => debounce((value: string) => {
    setSearchQuery(value);
  }, 500), [setSearchQuery]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setErrorMessage('');
    setSelectedList(null);
    onClose();
  }, [onClose]);

  const handleAddToList = useCallback(async () => {
    setErrorMessage('');

    if (!selectedList) {
      setErrorMessage(t('lists.add_to_list_modal.errors.no_list_selected'));
      return;
    }

    if (items.length === 0) {
      setErrorMessage(t('lists.add_to_list_modal.errors.empty_selection'));
      return;
    }

    try {
      await addItems({
        list_id: selectedList?.id as string,
        data: {
          entities: items,
        }
      }).unwrap();

      enqueueSnackbar(t('lists.api.add_to_list.success'), {
        variant: 'success',
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      enqueueSnackbar(t('lists.api.add_to_list.default_error'), {
        variant: 'error',
      });
    }
  }, [addItems, selectedList, items, t, onSuccess, handleClose]);

  const selectableOptions: SelectableOption[] = useMemo(() => {
    if (!lists || isListsLoading) {
      return [];
    }

    return lists.results.map((list) => ({
      id: list.id,
      name: list.name,
    }));

  }, [lists, isListsLoading]);

  const handleCreateList = () => {
    listEditorControl.onTrue();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('lists.add_to_list_modal.title')}
          </Typography>

          <Button variant="text" onClick={handleCreateList}>
            {t('lists.add_to_list_modal.create_list')}
          </Button>
        </Stack>

      </DialogTitle>

      <DialogContent>

        <Alert severity="info">
          {t('lists.add_to_list_modal.alert', {
            entityType: t(`lists.entity_types.${entityType}`),
            count: items.length
          })}
        </Alert>

        <Box sx={{ mt: 3 }}>
          <Stack spacing={3}>

            <Autocomplete
              sx={{ flexGrow: 1 }}
              multiple={false}
              options={selectableOptions}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.name}`
              }
              loading={isListsLoading}
              filterOptions={(options, params) => options.filter((option) => option.name)}
              value={selectedList}
              onChange={(_, value) => setSelectedList(value as SelectableOption)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('lists.search_placeholder')}
                />
              )}
              onInputChange={(_, value) => onSearch(value)}
              renderOption={({ key, ...rest }, option: SelectableOption) => (
                <li key={key} {...rest}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.name}`}
                    </Typography>
                  </Stack>
                </li>
              )}
            />

            {errorMessage && (
              <Alert severity="error">
                {errorMessage}
              </Alert>
            )}

          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          {t('common.cancel')}
        </Button>

        <LoadingButton
          type="submit"
          variant="contained"
          onClick={handleAddToList}
          disabled={isAddingToList}
          loading={isAddingToList}
        >
          {t('common.add')}
        </LoadingButton>
      </DialogActions>

      <ListEditorModal open={listEditorControl.value} onClose={listEditorControl.onFalse} entityType={entityType} />

    </Dialog>
  );
}
